"use client";

import { Mountain, Coffee, Route, Satellite, Landmark, Tent } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CompanyValuesGrid() {
  const {
    ref: headRef,
    isVisible: headVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const benefits = [{
    index: "B.01",
    iconName: "Mountain",
    title: "Certified HVAC Technicians",
    body: "Our team consists of fully licensed and insured professionals. Every technician undergoes rigorous training in modern climate control systems, ensuring precision, safety, and efficiency in every installation or repair."
  }, {
    index: "B.02",
    iconName: "Coffee",
    title: "Rapid Response Times",
    body: "We understand that a broken AC in the heat of summer is an emergency. Our local dispatch teams are strategically positioned across Sussex and Surrey to provide prompt, reliable service when you need it most."
  }, {
    index: "B.03",
    iconName: "Route",
    title: "Tailored Climate Solutions",
    body: "No two buildings are the same. We design bespoke cooling and heating strategies based on your specific square footage, insulation quality, and usage patterns to maximize your comfort and energy savings."
  }, {
    index: "B.04",
    iconName: "Satellite",
    title: "Advanced Diagnostic Tools",
    body: "We utilize state-of-the-art diagnostic technology to pinpoint issues before they become costly failures. Our proactive approach ensures your system runs at peak performance throughout the entire year."
  }, {
    index: "B.05",
    iconName: "Landmark",
    title: "Community-Focused Service",
    body: "As a local business, we take pride in serving our neighbors. We build long-term relationships with our clients, ensuring that every home and business we touch receives honest, transparent, and high-quality care."
  }, {
    index: "B.06",
    iconName: "Tent",
    title: "Premium Equipment Standards",
    body: "We only install industry-leading, energy-efficient hardware. Every component we use is field-tested for durability and performance, ensuring your investment provides reliable climate control for years to come."
  }];
  const iconMap: Record<string, React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>> = {
    Mountain,
    Coffee,
    Route,
    Satellite,
    Landmark,
    Tent
  };
  return <section data-section-id="1437" id="companyvaluesgrid" className="relative bg-slate-900 text-slate-50 py-32 md:py-44 overflow-hidden">
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-blue-950/20 border-t border-blue-900/30 overflow-hidden flex items-center">
        <div className="flex whitespace-nowrap animate-[ticker_40s_linear_infinite]">
          {[0, 1].map(dup => <div key={dup} data-index={dup} className="flex shrink-0">
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-slate-400 px-8">SUSSEX</span>
              <span className="font-mono text-xs text-blue-500 px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-slate-400 px-8">SURREY</span>
              <span className="font-mono text-xs text-blue-500 px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-slate-400 px-8">COMMERCIAL</span>
              <span className="font-mono text-xs text-blue-500 px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-slate-400 px-8">DOMESTIC</span>
              <span className="font-mono text-xs text-blue-500 px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-slate-400 px-8">MAINTENANCE</span>
              <span className="font-mono text-xs text-blue-500 px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-slate-400 px-8">INSTALLATION</span>
              <span className="font-mono text-xs text-blue-500 px-2">·</span>
              <span className="font-mono text-xs tracking-[0.14em] uppercase text-slate-400 px-8">CLIMATE CONTROL</span>
              <span className="font-mono text-xs text-blue-500 px-2">·</span>
            </div>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>

      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div ref={headRef} className={`lg:sticky lg:top-32 relative transition-all duration-[700ms] ease-out ${headVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)"
          }}>
              <div className="pointer-events-none select-none absolute -top-8 -left-2 font-sans italic font-light text-blue-500/[0.08] leading-none" style={{
              fontSize: "clamp(8rem, 15vw, 15rem)"
            }} aria-hidden="true">
                02
              </div>

              <p className="relative font-mono text-xs tracking-[0.14em] uppercase text-slate-400 mb-10">
                § 02 · COASTLINE EXCELLENCE — WHY CHOOSE US
              </p>

              <div className="relative flex items-center mb-10">
                <div className="h-px w-20 bg-blue-600" />
                <div className="w-1.5 h-1.5 bg-blue-600 rounded-lg" />
              </div>

              <h2 className="relative font-default font-light text-slate-50 leading-[0.96] tracking-[-0.025em] mb-10" style={{
              fontSize: "clamp(2.5rem, 5.5vw, 5rem)"
            }}>
                Why clients{" "}
                <span className="italic text-blue-500 font-light">trust us.</span>
              </h2>

              <p className="relative max-w-[320px] text-slate-300 leading-[1.55] mb-12" style={{
              fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)"
            }}>
                Our reputation is built on reliability and technical mastery. We don't just fix systems; we ensure your environment remains perfectly balanced, efficient, and comfortable throughout the changing seasons.
              </p>

              <Link to="/AirConditioningPage" className="relative inline-block font-default text-sm tracking-[0.06em] uppercase text-slate-50 underline decoration-blue-600 decoration-1 underline-offset-[6px] hover:underline-offset-[10px] transition-all duration-200 ease-out">
                View Our Services
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ul className="flex flex-col">
              {benefits.map((benefit, i) => {
              const Icon = iconMap[benefit.iconName];
              return <BenefitRow key={i} i={i} benefit={benefit} Icon={Icon} isLast={i === benefits.length - 1} />;
            })}
            </ul>
          </div>
        </div>
      </div>
    </section>;
}
function BenefitRow({
  i,
  benefit,
  Icon,
  isLast
}: {
  i: number;
  benefit: {
    index: string;
    title: string;
    body: string;
  };
  Icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  isLast: boolean;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLLIElement>({
    threshold: 0.15
  });
  return <li ref={ref} data-index={i} className={`group relative grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 py-10 md:py-12 transition-all duration-[700ms] ${i !== 0 ? "border-t border-slate-800" : ""} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
    transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
    transitionDelay: `${i * 90}ms`
  }}>
      <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-4 md:gap-6">
        <span className="font-mono text-xs tracking-[0.14em] uppercase text-blue-500">
          {benefit.index}
        </span>
        <div className="relative w-6 h-6 flex items-center justify-center">
          <Icon className="w-6 h-6 text-blue-500 transition-all duration-[260ms] ease-out group-hover:fill-blue-500/20" strokeWidth={1.25} />
        </div>
      </div>

      <div className="md:col-span-10">
        <h3 className="font-default font-normal text-slate-50 leading-[1.15] tracking-[-0.015em] mb-5 transition-transform duration-[260ms] ease-out group-hover:-translate-y-1" style={{
        fontSize: "clamp(1.375rem, 1.8vw, 1.75rem)"
      }}>
          {benefit.title}
        </h3>
        <p className="max-w-[480px] text-slate-400 leading-[1.6] mb-6" style={{
        fontSize: "clamp(0.9375rem, 1vw, 1rem)"
      }}>
          {benefit.body}
        </p>

        {!isLast && <div className="flex items-center">
            <div className="h-px w-20 bg-blue-600" />
            <div className="w-1.5 h-1.5 bg-blue-600 rounded-lg" />
          </div>}
      </div>
    </li>;
}